import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useSyncExternalStore } from "react";
import { resetPassword, validatePasswordReset } from "@relais/contracts";
import {
  supabase,
  subscribeRecovery,
  getRecoveryToken,
  recoveryRedirectPending,
} from "../lib/supabase.js";
import { useSession } from "../lib/auth.js";

export const Route = createFileRoute("/reset")({
  component: ResetPage,
});

const inputClass =
  "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring/40";

function ResetPage() {
  const navigate = useNavigate();
  const session = useSession();
  // Jeton recovery, réactif : re-rend si PASSWORD_RECOVERY arrive après le
  // premier rendu `authed`.
  const recoveryToken = useSyncExternalStore(
    subscribeRecovery,
    getRecoveryToken,
    getRecoveryToken,
  );
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authed = session.status === "authed";
  // Autorisation stricte : la session COURANTE doit être celle du lien recovery
  // (match d'access_token), pas juste « un recovery a eu lieu ».
  const isRecovery =
    authed &&
    recoveryToken !== null &&
    session.session.access_token === recoveryToken;
  // authed mais pas (encore) recovery, alors qu'un redirect recovery est en
  // cours → on attend que PASSWORD_RECOVERY pose le jeton (évite d'éjecter un
  // vrai user recovery vers /profil pendant la fenêtre de race).
  const waitingForRecovery = authed && !isRecovery && recoveryRedirectPending;
  // Session normale (déjà connecté, pas via lien recovery) → la voie gardée est
  // le PasswordDrawer du profil. Ne PAS ouvrir le form sans-ancien-mdp ici.
  const redirectToProfil = authed && !isRecovery && !recoveryRedirectPending;

  useEffect(() => {
    if (redirectToProfil) navigate({ to: "/profil" });
  }, [redirectToProfil, navigate]);

  if (session.status === "loading" || waitingForRecovery || redirectToProfil) {
    return <p className="text-sm text-muted-foreground">…</p>;
  }

  if (session.status === "anon") {
    return (
      <div className="max-w-sm mx-auto mt-8 space-y-4">
        <h2 className="text-xl font-semibold">Lien invalide ou expiré</h2>
        <p className="text-sm text-muted-foreground">
          Ce lien de réinitialisation n'est plus valide. Redemandez-en un depuis
          la page de connexion.
        </p>
        <Link to="/login" className="text-sm text-primary underline">
          Retour à la connexion
        </Link>
      </div>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validatePasswordReset({ next, confirm });
    if (invalid) {
      setError(
        invalid === "empty"
          ? "Saisissez un nouveau mot de passe."
          : "Les deux mots de passe ne correspondent pas.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    const res = await resetPassword(supabase, { next });
    setSaving(false);
    if (!res.ok) {
      setError(
        res.code === "update_failed"
          ? res.message
          : "Connexion interrompue. Vérifiez votre réseau et réessayez.",
      );
      return;
    }
    // Reset = récupération de compte : on révoque toutes les sessions (global)
    // AVANT de naviguer, pour ne pas laisser la session recovery vivante. Le
    // réseau vient de répondre (updateUser), donc l'await ne devrait pas traîner ;
    // `.catch` garantit qu'un échec de révocation serveur ne bloque pas la sortie
    // (signOut purge la session locale de toute façon). Le jeton recovery est
    // déjà consommé via l'event USER_UPDATED émis par updateUser.
    await supabase.auth.signOut({ scope: "global" }).catch(() => {});
    navigate({ to: "/login", search: { reset: 1 } });
  }

  return (
    <div className="max-w-sm mx-auto mt-8">
      <h2 className="text-xl font-semibold mb-6">Nouveau mot de passe</h2>
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="text-sm text-muted-foreground">Nouveau mot de passe</span>
          <input
            autoFocus
            required
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="text-sm text-muted-foreground">Confirmer</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
          />
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={saving}
          className="w-full rounded-md bg-primary text-primary-foreground py-2 disabled:opacity-50"
        >
          {saving ? "…" : "Réinitialiser"}
        </button>
      </form>
    </div>
  );
}
